import type { PermitTypeFixedInterval } from "@offstreetparking/domain/rules";
import { DateTime } from "luxon";

export type ProrationInterval = PermitTypeFixedInterval;

type ProrationResult = {
	isProrated: boolean;
	fullTermStartISO?: string;
	fullTermEndISO?: string;
};

type GetProrationArgs = {
	fixedIntervals: ProrationInterval[] | undefined | null;
	timezone: string | undefined | null;
	now?: DateTime;
};

export const getProration = ({ fixedIntervals, timezone, now }: GetProrationArgs): ProrationResult => {
	if (!fixedIntervals || fixedIntervals.length === 0 || !timezone) {
		return { isProrated: false };
	}

	let earliestStart: DateTime | undefined;
	let latestEnd: DateTime | undefined;

	for (const interval of fixedIntervals) {
		const start = DateTime.fromISO(interval.startDateTime);
		if (start.isValid && (!earliestStart || start < earliestStart)) {
			earliestStart = start;
		}

		if (interval.endDateTime) {
			const end = DateTime.fromISO(interval.endDateTime);
			if (end.isValid && (!latestEnd || end > latestEnd)) {
				latestEnd = end;
			}
		}
	}

	if (!earliestStart || !latestEnd) {
		return { isProrated: false };
	}

	const fullTermStart = earliestStart.setZone(timezone);
	const fullTermEnd = latestEnd.setZone(timezone);
	const today = (now ?? DateTime.now()).setZone(timezone).startOf("day");
	const isProrated = today > fullTermStart.startOf("day") && today <= fullTermEnd.startOf("day");

	return {
		isProrated,
		fullTermStartISO: fullTermStart.toISO() ?? undefined,
		fullTermEndISO: fullTermEnd.toISO() ?? undefined,
	};
};
